import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import refundService, { RefundHistory, RiderWallet } from '../services/refundService';

const steps = ['Enter Rider Public ID', 'Review Wallet', 'Enter Refund Details', 'Confirm'];

const Refund: React.FC = () => {
  const [refundHistory, setRefundHistory] = useState<RefundHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [riderPublicId, setRiderPublicId] = useState('');
  const [riderWallet, setRiderWallet] = useState<RiderWallet | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [isFullRefund, setIsFullRefund] = useState(false);
  const [transactionDate, setTransactionDate] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Load refund history on component mount
  useEffect(() => {
    loadRefundHistory();
  }, []);

  const loadRefundHistory = async () => {
    setLoading(true);
    try {
      const history = await refundService.getRefundHistory();
      setRefundHistory(history);
    } catch (error) {
      showSnackbar('Failed to load refund history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setActiveStep(0);
    resetForm();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActiveStep(0);
    resetForm();
  };

  const resetForm = () => {
    setRiderPublicId('');
    setRiderWallet(null);
    setRefundAmount('');
    setIsFullRefund(false);
    setTransactionDate('');
    setUtrNumber('');
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate rider public ID and fetch wallet
      if (!riderPublicId.trim()) {
        showSnackbar('Please enter rider public ID', 'error');
        return;
      }

      try {
        const wallet = await refundService.getRiderWalletByPublicId(riderPublicId.trim());
        setRiderWallet(wallet);
        setActiveStep(1);
      } catch (error) {
        showSnackbar('Rider not found or wallet not available', 'error');
      }
    } else if (activeStep === 1) {
      // Move to refund details
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Validate refund details
      const amount = isFullRefund && riderWallet ? riderWallet.wallet.totalSd : parseFloat(refundAmount);
      if (!amount || amount <= 0) {
        showSnackbar('Please enter a valid refund amount', 'error');
        return;
      }

      if (riderWallet && amount > riderWallet.wallet.totalSd) {
        showSnackbar('Refund amount cannot exceed total SD balance', 'error');
        return;
      }

      if (!transactionDate) {
        showSnackbar('Please select a transaction date', 'error');
        return;
      }

      if (!utrNumber.trim()) {
        showSnackbar('UTR number is required', 'error');
        return;
      }

      setActiveStep(3);
    } else if (activeStep === 3) {
      // Submit refund
      await handleSubmitRefund();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmitRefund = async () => {
    if (!riderWallet) return;

    try {
      await refundService.createRefund({
        riderId: riderWallet.rider.id,
        amount: isFullRefund ? riderWallet.wallet.totalSd : parseFloat(refundAmount),
        transactionDate: new Date(transactionDate).toISOString(),
        utrNumber: utrNumber.trim(),
        fullRefund: isFullRefund,
      });

      showSnackbar('Refund processed successfully', 'success');
      handleCloseDialog();
      loadRefundHistory(); // Refresh the history
    } catch (error) {
      showSnackbar('Failed to process refund', 'error');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Refund Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Apply Refund
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <b>Note :</b> Rider must work for at least 3 months (90 days) in our system to qualify for a full Sd refund.
      </Alert>

      {/* Refund History Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Refund History
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rider ID</TableCell>
                    <TableCell>Rider Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Transaction Date</TableCell>
                    <TableCell>UTR Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {refundHistory.map((refund) => (
                    <TableRow key={refund.id}>
                      <TableCell>{refund.riderPublicId || 'N/A'}</TableCell>
                      <TableCell>{refund.rider.name || 'N/A'}</TableCell>
                      <TableCell>{refund.rider.phone}</TableCell>
                      <TableCell>{formatCurrency(refund.amount)}</TableCell>
                      <TableCell>{refund.transactionDate ? formatDate(refund.transactionDate) : 'N/A'}</TableCell>
                      <TableCell>{refund.utrNumber || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={refund.status}
                          color={getStatusColor(refund.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(refund.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                  {refundHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No refund history found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Refund Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Apply Refund</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Enter Rider Public ID */}
          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Rider Public ID"
                value={riderPublicId}
                onChange={(e) => setRiderPublicId(e.target.value)}
                placeholder="Enter rider public ID"
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {/* Step 2: Review Wallet */}
          {activeStep === 1 && riderWallet && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Rider Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rider Public ID"
                    value={riderWallet.rider.publicRiderId || ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={riderWallet.rider.name || ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={riderWallet.rider.phone}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>
                Wallet Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total SD"
                    value={formatCurrency(riderWallet.wallet.totalSd)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Pending SD"
                    value={formatCurrency(riderWallet.wallet.pendingSd)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Enter Refund Details */}
          {activeStep === 2 && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFullRefund}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsFullRefund(checked);
                      if (checked && riderWallet) {
                        setRefundAmount(riderWallet.wallet.totalSd.toString());
                      }
                      if (!checked) {
                        setRefundAmount('');
                      }
                    }}
                  />
                }
                label="Refund full SD balance and exit the system"
                sx={{ mb: 2, display: 'block' }}
              />
              {isFullRefund && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Rider must work for at least 3 months (90 days) in our system to qualify for a full refund.
                </Alert>
              )}
              <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Enter refund amount"
                sx={{ mb: 2 }}
                helperText={
                  isFullRefund
                    ? 'Full refund selected - total SD and pending SD will become zero because rider is exiting the system.'
                    : `Maximum allowed: ${riderWallet ? formatCurrency(riderWallet.wallet.totalSd) : 'N/A'}`
                }
                disabled={isFullRefund}
              />
              <TextField
                fullWidth
                label="Transaction Date"
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                sx={{ mb: 2 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="UTR Number"
                value={utrNumber}
                onChange={(e) => setUtrNumber(e.target.value)}
                placeholder="Enter UTR number"
              />
            </Box>
          )}

          {/* Step 4: Confirm */}
          {activeStep === 3 && riderWallet && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirm Refund Details
              </Typography>
              {isFullRefund && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Full refund is selected. Rider will exit the system and both Total SD and Pending SD balances will be set to zero.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rider"
                    value={`${riderWallet.rider.name || 'N/A'} (${riderWallet.rider.publicRiderId})`}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Amount"
                    value={formatCurrency(parseFloat(refundAmount))}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Transaction Date"
                    value={transactionDate}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="UTR Number"
                    value={utrNumber || 'N/A'}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Button variant="contained" onClick={handleNext}>
            {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Refund;